import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Drawer, List, Tag, Tooltip, Typography, Space, Segmented, Divider, Alert, Collapse } from 'antd';
import { CircleHelp, ExternalLink, ChevronLeft, ChevronRight, User, Clock, TriangleAlert, Terminal, Link2 } from 'lucide-react';
import { useTheme } from '@/App';
import script from '@/config/demoScript.json';

/*
 * DemoGuide — in-app companion for the presenter's script.
 *
 * A small "?" floating button (bottom-left, away from the chat bubble) that only renders when
 * `localStorage.demoMode === '1'`. It carries the four sections of the script: the 20-minute run
 * (scene by scene, with one-click navigation), the before-you-start setup, the deeper dives and the
 * "if they ask" answers — plus the known rough edges, which are also surfaced inline on the scenes
 * they can ambush.
 *
 * Content comes from src/config/demoScript.json. THAT FILE IS HAND-MAINTAINED, not generated: it is
 * kept in step by hand with bmr_docs/backend/djangoapp/refactor_tracking/demo_script.md. There is no
 * generator any more (`gen_demo_docs.py` no longer exists in any repo) — edit the JSON and the
 * markdown together.
 */

const { Text, Paragraph } = Typography;

const readFlag = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};
const writeFlag = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    /* storage blocked — fine */
  }
};

/*
 * How well does `pathname` match a scene's `route`? Higher wins; -1 is no match.
 * Four scenes sit on `/batch-process-details/<batch>` and differ only by batch number, so an exact
 * match must always beat the bare-prefix fallback — otherwise every batch page selects the first of
 * the four.
 */
const routeScore = (pathname, route) => {
  if (!route) return -1;
  if (route === '/') return pathname === '/' || pathname === '' ? 1000 : -1;
  if (pathname === route) return 1000 + route.length; // exact — including the batch number
  const base = route.replace(/\/[A-Z0-9]{6,}$/, ''); // strip a batch number placed in the scene route
  if (base !== route && (pathname === base || pathname.startsWith(`${base}/`))) return base.length; // same family, other object
  if (pathname.startsWith(`${route}/`)) return route.length;
  return -1;
};

const personaOf = (username) => script.personas.find((p) => p.username === username) || { username, role: '' };

const allEdges = [...script.rough_edges.steer_around, ...script.rough_edges.empty_by_design];
const edgeOf = (id) => allEdges.find((e) => e.id === id);

export default function DemoGuide() {
  const [enabled, setEnabled] = useState(() => readFlag('demoMode') === '1');
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState('run');
  const [index, setIndex] = useState(() => Number(readFlag('demoScene') || 0));
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Pick the flag up when it is toggled from another tab / the console without a reload.
  useEffect(() => {
    const check = () => setEnabled(readFlag('demoMode') === '1');
    window.addEventListener('storage', check);
    window.addEventListener('focus', check);
    return () => {
      window.removeEventListener('storage', check);
      window.removeEventListener('focus', check);
    };
  }, []);

  const scenes = script.scenes;

  // Follow the router: when the user lands on a scene's route, make it the current scene.
  useEffect(() => {
    const sceneScore = (s) => {
      if (s.app !== 'admin') return -1;
      const primary = routeScore(location.pathname, s.route);
      // A scene's secondary links (the cockpit's Control Charts, Stage 4's Record Exports, …) also
      // claim their route, but always behind a scene that owns it outright.
      const secondary = (s.links || []).reduce((best, l) => Math.max(best, routeScore(location.pathname, l.route)), -1);
      return Math.max(primary, secondary >= 0 ? secondary - 500 : -1);
    };
    const hits = scenes
      .map((s, i) => ({ i, score: sceneScore(s) }))
      .filter((h) => h.score >= 0)
      .sort((a, b) => b.score - a.score);
    if (hits.length) setIndex(hits[0].i);
  }, [location.pathname, scenes]);

  useEffect(() => {
    writeFlag('demoScene', String(index));
  }, [index]);

  const current = scenes[index] || scenes[0];
  const persona = personaOf(current.persona);
  const totalMin = useMemo(() => scenes.reduce((a, s) => a + (s.minutes || 0), 0), [scenes]);

  if (!enabled) return null;

  const bg = isDark ? '#1e293b' : '#ffffff';
  const fg = isDark ? '#e2e8f0' : '#0f172a';
  const muted = isDark ? '#334155' : '#e2e8f0';
  const accent = '#7c3aed';

  const goRoute = (route) => {
    if (route) navigate(route);
  };
  const go = (scene) => {
    setIndex(scenes.indexOf(scene));
    if (scene.app === 'admin') goRoute(scene.route);
    else if (scene.app === 'operator') window.open(script.apps.operator.base, '_blank', 'noopener');
  };
  const step = (delta) => {
    const next = scenes[Math.min(scenes.length - 1, Math.max(0, index + delta))];
    if (next) go(next);
  };

  const Mono = ({ children }) => (
    <code style={{ display: 'block', fontSize: 11, lineHeight: 1.5, padding: '4px 6px', borderRadius: 4, background: isDark ? '#0f172a' : '#f1f5f9', color: fg, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{children}</code>
  );

  const WarningList = ({ ids }) => {
    const edges = (ids || []).map(edgeOf).filter(Boolean);
    if (!edges.length) return null;
    return (
      <div style={{ marginTop: 10 }}>
        {edges.map((e) => (
          <Alert
            key={e.id}
            type="warning"
            showIcon
            icon={<TriangleAlert size={14} />}
            style={{ marginBottom: 6, padding: '6px 10px' }}
            message={<span style={{ fontSize: 12, fontWeight: 600 }}>{e.title}</span>}
            description={
              <span style={{ fontSize: 11 }}>
                {e.detail}
                {e.if_asked ? (
                  <>
                    <br />
                    <Text type="secondary" style={{ fontSize: 11 }}>If asked: {e.if_asked}</Text>
                  </>
                ) : null}
              </span>
            }
          />
        ))}
      </div>
    );
  };

  const LinkRow = ({ links }) =>
    links && links.length ? (
      <Space size={6} wrap style={{ marginTop: 8 }}>
        {links.map((l) => (
          <Button key={l.route} size="small" icon={<Link2 size={12} />} onClick={() => goRoute(l.route)}>
            {l.label}
          </Button>
        ))}
      </Space>
    ) : null;

  const ApiRow = ({ api }) =>
    api && api.length ? (
      <div style={{ marginTop: 8 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          <Terminal size={11} style={{ verticalAlign: -1, marginRight: 4 }} />
          API
        </Text>
        {api.map((a) => (
          <Mono key={a}>{a}</Mono>
        ))}
      </div>
    ) : null;

  const runSection = (
    <>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${muted}` }}>
        <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
          <Button size="small" icon={<ChevronLeft size={14} />} onClick={() => step(-1)} disabled={index <= 0}>Prev</Button>
          <Text type="secondary" style={{ fontSize: 12 }}>Scene {index + 1} of {scenes.length} · {totalMin} min total</Text>
          <Button size="small" icon={<ChevronRight size={14} />} iconPosition="end" onClick={() => step(1)} disabled={index >= scenes.length - 1}>Next</Button>
        </Space>
        <Paragraph strong style={{ margin: '10px 0 4px', color: fg, fontSize: 15 }}>{current.at} — {current.title}</Paragraph>
        <Space size={6} wrap>
          <Tag icon={<Clock size={11} style={{ marginRight: 4 }} />}>{current.minutes} min</Tag>
          <Tag color={current.app === 'operator' ? 'cyan' : current.app === 'none' ? 'default' : 'blue'}>{script.apps[current.app].label}</Tag>
          {current.route ? <Tag color="purple">{current.route}</Tag> : null}
        </Space>
        <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: isDark ? '#312e81' : '#f5f3ff', color: isDark ? '#e0e7ff' : '#3730a3' }}>
          <Space size={6} align="start">
            <User size={14} style={{ marginTop: 3 }} />
            <div>
              <Text strong style={{ color: 'inherit' }}>Be logged in as {persona.username}</Text>
              <div style={{ fontSize: 12 }}>{persona.name ? `${persona.name} — ` : ''}{persona.role} · password {script.password}</div>
            </div>
          </Space>
        </div>
        <Paragraph style={{ margin: '10px 0 4px', color: fg, fontSize: 12 }}><Text type="secondary">Where:</Text> {current.menu}</Paragraph>
        <Paragraph style={{ margin: '0 0 4px', color: fg, fontSize: 12 }}><Text type="secondary">Data:</Text> {current.data}</Paragraph>
        <ol style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 12, color: fg }}>
          {current.clicks.map((c, i) => (
            <li key={i} style={{ marginBottom: 3 }}>{c}</li>
          ))}
        </ol>
        <LinkRow links={current.links} />
        <ApiRow api={current.api} />
        <Paragraph italic style={{ margin: '8px 0 0', color: fg, fontSize: 12, borderLeft: `3px solid ${accent}`, paddingLeft: 8 }}>“{current.say}”</Paragraph>
        <WarningList ids={current.warnings} />
        <Paragraph style={{ margin: '6px 0 0', fontSize: 11 }} type="secondary"><Text type="secondary">If it fails:</Text> {current.fallback}</Paragraph>
      </div>
      <Divider style={{ margin: '8px 0 0' }} />
      <List
        size="small"
        dataSource={scenes}
        renderItem={(s) => {
          const active = s === current;
          return (
            <List.Item
              onClick={() => go(s)}
              style={{ cursor: 'pointer', padding: '8px 16px', background: active ? (isDark ? '#312e81' : '#ede9fe') : 'transparent', borderLeft: active ? `3px solid ${accent}` : '3px solid transparent' }}
            >
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Space size={6}>
                  <Text strong style={{ color: fg, fontSize: 13 }}>{s.at} · {s.title}</Text>
                  {s.app === 'operator' && <ExternalLink size={12} />}
                  {(s.warnings || []).length ? <TriangleAlert size={12} color="#d97706" /> : null}
                </Space>
                <Text type="secondary" style={{ fontSize: 11 }}>{s.persona} · {s.minutes} min · {s.route || 'no screen'}</Text>
              </Space>
            </List.Item>
          );
        }}
      />
    </>
  );

  const setup = script.setup;
  // The four staged batches, side by side — which stage each one is, what it shows,
  // which header toggle it needs, and the rough edge that can ambush you on it.
  const stagesSection = (
    <div style={{ padding: '12px 16px', color: fg, fontSize: 12 }}>
      <Paragraph style={{ fontSize: 12, color: fg }}>
        Four batches, each parked at a different point in the lifecycle, plus the deliberately
        bad one kept for the deep review. Open each from here — the guide follows you.
      </Paragraph>
      {(setup.batches || []).map((b) => (
        <div
          key={b.batch}
          style={{ border: `1px solid ${muted}`, borderRadius: 8, padding: 10, marginBottom: 10 }}
        >
          <Space wrap size={6} style={{ marginBottom: 4 }}>
            <Tag color={b.stage === 'Reference' ? 'purple' : 'blue'}>{b.stage || '—'}</Tag>
            <Text strong style={{ color: fg }}>{b.batch}</Text>
            <Tag>{b.toggle}</Tag>
            {b.scene && <Tag icon={<Clock size={11} />}>{b.scene}</Tag>}
          </Space>
          <div style={{ color: fg, marginBottom: 4 }}>{b.stage_name} — {b.point || b.shows}</div>
          {(b.show || []).map((line, i) => (
            <div key={i} style={{ opacity: 0.75, marginLeft: 8 }}>· {line}</div>
          ))}
          {b.watch && (
            <Alert
              type="warning"
              showIcon
              icon={<TriangleAlert size={13} />}
              style={{ marginTop: 6, fontSize: 11.5 }}
              message={b.watch}
            />
          )}
          <Button
            size="small"
            type="link"
            icon={<ExternalLink size={12} />}
            style={{ paddingLeft: 0, marginTop: 4 }}
            onClick={() => { navigate(`/batch-process-details/${b.batch}`); setOpen(false); }}
          >
            Open {b.batch}
          </Button>
        </div>
      ))}
    </div>
  );

  const setupSection = (
    <div style={{ padding: '12px 16px', color: fg, fontSize: 12 }}>
      <Paragraph style={{ fontSize: 12, color: fg }}>{script.intro}</Paragraph>
      <Text strong style={{ color: fg }}>Bring the stack up</Text>
      {setup.stack.map((s) => (
        <div key={s.label} style={{ margin: '6px 0' }}>
          <Text strong style={{ color: fg, fontSize: 12 }}>{s.label}</Text>
          <Mono>{s.command}</Mono>
          <Text type="secondary" style={{ fontSize: 11 }}>{s.check}</Text>
        </div>
      ))}
      {setup.stack_notes.map((n, i) => (
        <Paragraph key={i} style={{ fontSize: 11, color: fg, marginBottom: 6 }}>{n}</Paragraph>
      ))}
      <Divider style={{ margin: '10px 0' }} />
      <Text strong style={{ color: fg }}>Seed the data</Text>
      {setup.seed.commands.map((c) => (
        <Mono key={c}>{c}</Mono>
      ))}
      {setup.seed.notes.map((n, i) => (
        <Paragraph key={i} style={{ fontSize: 11, color: fg, marginBottom: 6 }}>{n}</Paragraph>
      ))}
      <Divider style={{ margin: '10px 0' }} />
      <Text strong style={{ color: fg }}>{setup.toggles.headline}</Text>
      <Paragraph style={{ fontSize: 11, color: fg, margin: '4px 0' }}>{setup.toggles.intro}</Paragraph>
      {setup.toggles.switches.map((s) => (
        <Paragraph key={s.toggle} style={{ fontSize: 11, color: fg, margin: '0 0 4px' }}>
          <Tag color="purple">{s.toggle}</Tag> {s.settings} — {s.filters}
        </Paragraph>
      ))}
      {setup.toggles.matrix.map((m) => (
        <Paragraph key={m.want} style={{ fontSize: 11, color: fg, margin: '0 0 4px' }}>
          {m.want} → <Text strong style={{ color: fg, fontSize: 11 }}>{m.set}</Text>
        </Paragraph>
      ))}
      <Alert type="info" showIcon style={{ padding: '6px 10px' }} message={<span style={{ fontSize: 11 }}>{setup.toggles.note}</span>} />
      <Divider style={{ margin: '10px 0' }} />
      <Text strong style={{ color: fg }}>Logins</Text>
      <Paragraph style={{ fontSize: 11, color: fg, margin: '4px 0 8px' }}>{setup.logins_note}</Paragraph>
      {script.personas.map((p) => (
        <Paragraph key={p.username} style={{ fontSize: 11, color: fg, margin: '0 0 3px' }}>
          <Text code style={{ fontSize: 11 }}>{p.username}</Text> — {p.name} · {p.demos}
        </Paragraph>
      ))}
      <Divider style={{ margin: '10px 0' }} />
      <Text strong style={{ color: fg }}>The five batches</Text>
      {setup.batches.map((b) => (
        <Paragraph key={b.batch} style={{ fontSize: 11, color: fg, margin: '4px 0 0' }}>
          <Button type="link" size="small" style={{ padding: 0, fontSize: 11 }} onClick={() => goRoute(`/batch-process-details/${b.batch}`)}>{b.batch}</Button>
          {' '}— {b.state} · <Tag style={{ fontSize: 10 }}>{b.toggle}</Tag> {b.shows}
        </Paragraph>
      ))}
      <Alert type="warning" showIcon icon={<TriangleAlert size={14} />} style={{ marginTop: 8, padding: '6px 10px' }} message={<span style={{ fontSize: 11 }}>{setup.batches_note}</span>} />
    </div>
  );

  const divesSection = (
    <div style={{ padding: '12px 16px', color: fg, fontSize: 12 }}>
      <Paragraph type="secondary" style={{ fontSize: 11 }}>Take these only if asked. Each is self-contained.</Paragraph>
      <Collapse
        size="small"
        ghost
        items={script.deep_dives.map((d) => ({
          key: d.id,
          label: <Text strong style={{ color: fg, fontSize: 12 }}>{d.id} · {d.title}</Text>,
          children: (
            <>
              <Text type="secondary" style={{ fontSize: 11 }}>{d.persona}{d.route ? ` · ${d.route}` : ''}</Text>
              {d.body.map((b, i) => (
                <Paragraph key={i} style={{ fontSize: 11, color: fg, margin: '6px 0 0' }}>{b}</Paragraph>
              ))}
              <ApiRow api={d.api} />
              <LinkRow links={[...(d.route ? [{ label: `Open ${d.route}`, route: d.route }] : []), ...(d.links || [])]} />
              {d.say ? (
                <Paragraph italic style={{ margin: '8px 0 0', color: fg, fontSize: 11, borderLeft: `3px solid ${accent}`, paddingLeft: 8 }}>“{d.say}”</Paragraph>
              ) : null}
              <WarningList ids={d.warnings} />
            </>
          )
        }))}
      />
    </div>
  );

  const faqSection = (
    <div style={{ padding: '12px 16px', color: fg }}>
      <Collapse
        size="small"
        ghost
        items={script.faq.map((f, i) => ({
          key: String(i),
          label: <Text strong style={{ color: fg, fontSize: 12 }}>{f.q}</Text>,
          children: <Paragraph style={{ fontSize: 11, color: fg, margin: 0 }}>{f.a}</Paragraph>
        }))}
      />
    </div>
  );

  const edgesSection = (
    <div style={{ padding: '12px 16px', color: fg }}>
      <Paragraph type="secondary" style={{ fontSize: 11 }}>{script.rough_edges.intro}</Paragraph>
      <Text strong style={{ color: fg, fontSize: 12 }}>Steer around ({script.rough_edges.steer_around.length})</Text>
      <div style={{ marginTop: 6 }}>
        <WarningList ids={script.rough_edges.steer_around.map((e) => e.id)} />
      </div>
      <Divider style={{ margin: '10px 0' }} />
      <Text strong style={{ color: fg, fontSize: 12 }}>Empty by design, not broken</Text>
      {script.rough_edges.empty_by_design.map((e) => (
        <Paragraph key={e.id} style={{ fontSize: 11, color: fg, margin: '6px 0 0' }}>
          <Text strong style={{ color: fg, fontSize: 11 }}>{e.title}</Text>
          {e.detail ? ` — ${e.detail}` : ''}
        </Paragraph>
      ))}
      <Divider style={{ margin: '10px 0' }} />
      <Paragraph type="secondary" style={{ fontSize: 10 }}>{script._maintenance}</Paragraph>
    </div>
  );

  const bodies = { run: runSection, stages: stagesSection, setup: setupSection, dives: divesSection, faq: faqSection, edges: edgesSection };

  return (
    <>
      <Tooltip title={`Demo guide — ${current.at} ${current.title}`} placement="right">
        <Button
          type="primary"
          shape="circle"
          aria-label="Open demo guide"
          onClick={() => setOpen(true)}
          icon={<CircleHelp size={20} />}
          style={{ position: 'fixed', left: 18, bottom: 18, zIndex: 1200, width: 44, height: 44, background: accent, borderColor: accent, boxShadow: '0 6px 18px rgba(124,58,237,.35)' }}
        />
      </Tooltip>
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="left"
        width={460}
        title={
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text strong style={{ color: fg }}>{script.title}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>v{script.version} · {script.run_minutes}-minute run · verified {script.verified_on} · hand-maintained</Text>
            <Segmented
              size="small"
              value={section}
              onChange={setSection}
              options={[
                { label: 'Run', value: 'run' },
                { label: `Stages (${(setup.batches || []).length})`, value: 'stages' },
                { label: 'Setup', value: 'setup' },
                { label: 'Dives', value: 'dives' },
                { label: 'If they ask', value: 'faq' },
                { label: `Edges (${script.rough_edges.steer_around.length})`, value: 'edges' }
              ]}
            />
          </Space>
        }
        styles={{ body: { padding: 0, background: bg, color: fg }, header: { background: bg } }}
      >
        {bodies[section]}
      </Drawer>
    </>
  );
}
